import React from 'react';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import Box from '@mui/system/Box';
import styled from '@mui/system/styled';
import { COLORS } from '@styles';

const lightStyles = {
  root: {
    border: `1px solid ${COLORS.NEUTRAL_LIGHT_2}`,
    backgroundColor: COLORS.NEUTRAL_WHITE,
  },
  input: { color: COLORS.NEUTRAL_DARK_2 },
  placeholder: { color: COLORS.NEUTRAL_LIGHT_0 },
  label: { color: COLORS.NEUTRAL_DARK_2 },
};

const darkStyles = {
  root: {
    border: `1px solid ${COLORS.NEUTRAL_DARK_1}`,
    backgroundColor: "transparent",
  },
  input: { color: COLORS.NEUTRAL_LIGHT_3 },
  placeholder: { color: COLORS.NEUTRAL_LIGHT_0 },
  label: { color: COLORS.NEUTRAL_LIGHT_3 },
};

const StyledPhoneInput = styled(PhoneInput)<{ variant?: 'light' | 'dark' }>(({ variant = 'light' }) => {
  const isDark = variant === 'dark';
  const s = isDark ? darkStyles : lightStyles;
  return {
    '&.PhoneInput': {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      borderRadius: '16px',
      padding: '4px 16px',
      height: '48px',
      boxSizing: 'border-box',
      ...s.root,
    },
    '& .PhoneInputInput': {
      border: 0,
      background: 'transparent',
      outline: 'none',
      padding: 0,
      margin: 0,
      height: '100%',
      flex: 1,
      fontFamily: 'Inter, sans-serif',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '24px',
      ...s.input,
    },
    '& .PhoneInputInput::placeholder': {
      ...s.placeholder,
      opacity: 1,
    },
    '& .PhoneInputCountry': {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      marginRight: '8px',
      color: s.input.color,
    },
    '& .PhoneInputCountrySelectArrow': {
      color: s.input.color,
      opacity: 0.8,
    },
  };
});

interface PhoneNumberInputProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
  inputId: string;
  label?: string;
  name?: string;
  onCountryChange?: (country: string) => void;
  country?: string;
  variant?: 'light' | 'dark';
}

const sx = (
  variant: 'light' | 'dark' = 'light',
): {
  inputContainer: Record<string, unknown>;
  labelDefault: React.CSSProperties;
} => ({
  inputContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
    alignItems: 'flex-start',
    width: '100%',
  },
  labelDefault: {
    color: variant === 'dark' ? darkStyles.label.color : lightStyles.label.color,
    fontFamily: 'Inter, sans-serif',
    fontSize: '0.75rem',
    fontWeight: 600,
    lineHeight: '1rem',
    paddingLeft: '0.75rem',
  },
});

export function PhoneNumberInput({
  value,
  onChange,
  inputId,
  label,
  name,
  onCountryChange,
  country,
  variant = 'light',
}: PhoneNumberInputProps) {
  const styles = sx(variant);
  return (
    <Box sx={styles.inputContainer}>
      {label && (
        <label htmlFor={inputId} style={styles.labelDefault}>
          {label}
        </label>
      )}

      <StyledPhoneInput
        variant={variant}
        defaultCountry="CA"
        country={country}
        countries={['CA', 'US', 'GB', 'NZ', 'AU', 'BR']}
        value={value}
        onChange={onChange}
        onCountryChange={onCountryChange ? (c) => c != null && onCountryChange(c) : undefined}
        id={inputId}
        name={name}
        addInternationalOption={false}
        maxLength={15}
      />
    </Box>
  );
}
